import { mutate, useSWRConfig } from "swr"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { getHotDeals, HotDealsResponse } from "@lib/api/hot-deals"
import { Listing } from "@t/Listing"
import { useSearchParams } from "next/navigation"
import { useDealsContext } from "../context"
import { getParamsFromSearch } from "./DealsContext"

export const useDeals = (getKey: (page: number) => string) => {
    const key = getKey(1)
    const { cache } = useSWRConfig()
    const [deals, setDeals] = useState<HotDealsResponse[]>(() => {
        const res = cache.get("hotDealsCache")
        if (res?.data?.key === key) {
            return res.data.deals
        }
        return []
    })
    const search = useSearchParams()
    const [isLoading, setIsLoading] = useState(false)
    const [page, setPage] = useState(1)
    const abortRef = useRef(null)
    const initialRef = useRef(true)
    const loadMoreRef = useRef({ page, isLoading, deals })
    loadMoreRef.current = { page, isLoading, deals }
    const { setState, ...contextState } = useDealsContext()
    const contextStateRef = useRef(contextState)
    contextStateRef.current = contextState
    useEffect(() => {
        const params = getParamsFromSearch(search)
        // track redirects back/forward on hot deals page to update context
        if (JSON.stringify(params) !== JSON.stringify(contextStateRef.current)) {
            setState(params)
        }
    }, [search, setState])

    useEffect(() => {
        if (initialRef.current) return
        mutate("hotDealsCache", { deals, key }, false)
    }, [deals, key])
    useEffect(() => {
        let skip = false
        if (initialRef.current) {
            initialRef.current = false
            if (loadMoreRef.current.deals?.[0]) {
                skip = true
            }
        }
        if (!key || skip) return
        if (abortRef.current) {
            abortRef.current.abort("Stopped")
            abortRef.current = null
        }
        abortRef.current = new AbortController()
        setIsLoading(true)
        setPage(1)
        getHotDeals(key, abortRef.current.signal)
            .then((data) => setDeals([data]))
            .finally(() => setIsLoading(false))
        return () => abortRef.current.abort("Stopped")
    }, [key])
    const loadMore = useCallback(() => {
        if (loadMoreRef.current.isLoading || loadMoreRef.current.deals?.[0].pagination?.pageCount <= loadMoreRef.current.page) return
        loadMoreRef.current.page++
        setPage(loadMoreRef.current.page)
        setIsLoading(true)
        loadMoreRef.current.isLoading = true
        abortRef.current = new AbortController()
        getHotDeals(getKey(loadMoreRef.current.page), abortRef.current.signal)
            .then((data) => setDeals((s) => [...s, data]))
            .finally(() => setIsLoading(false))
    }, [getKey])
    return {
        deals: useMemo(() => {
            const d = deals.map((i) => i.results).flat()
            const uniqueMap = new Map()
            d.forEach((i) => {
                if (uniqueMap.has(i.id)) {
                    uniqueMap.delete(i.id)
                }
                uniqueMap.set(i.id, i)
            })
            return Array.from(uniqueMap.values()) as Listing[]
        }, [deals]),
        isLoading,
        hasMore: deals?.length && deals[0]?.pagination?.pageCount > page,
        loadMore
    }
}
